import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  ProgressBar,
  Spinner,
} from "react-bootstrap";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AppsOutlined from "@mui/icons-material/AppsOutlined";
import SecurityOutlined from "@mui/icons-material/SecurityOutlined";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import PageLayout from "../../Components/PageLayout";
import { Autocomplete, Checkbox, FormControlLabel, TextField, Typography, Box } from "@mui/material";
import { toast } from "react-toastify";
import styles from "../CreateEnquiryWizard.module.scss";
import {
  fetchNavApps,
  fetchAdminDefaultApps,
  createNavApp,
  updateNavApp,
  deleteNavApp,
  createMenuItem,
  deleteMenuItem,
  setRoleDefaultApp,
  setUserDefaultApp,
} from "../../api/navAppsApi";
import { NAV_ICON_OPTIONS, getNavIconComponent } from "../../navigation/muiNavIcons";
import {
  EMS_ROUTE_CATALOG,
  searchRouteCatalog,
  groupCatalogByCategory,
} from "../../navigation/emsRouteCatalog";

const TOTAL_STEPS = 4;

const STEP_META = [
  {
    id: 1,
    short: "App",
    title: "Name & icon",
    icon: <AppsOutlined fontSize="small" />,
  },
  {
    id: 2,
    short: "Access",
    title: "Who can use it",
    icon: <SecurityOutlined fontSize="small" />,
  },
  {
    id: 3,
    short: "Pages",
    title: "Sidebar links",
    icon: <LinkOutlined fontSize="small" />,
  },
  {
    id: 4,
    short: "Review",
    title: "Confirm",
    icon: <CheckCircleRoundedIcon fontSize="small" />,
  },
];

/** Values normalized for backend `role_access` (lowercase). */
const EMS_ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "counsellor", label: "Counsellor" },
  { value: "visa counsellor", label: "Visa Counsellor" },
  { value: "visa&application counsellor", label: "Visa & Application Counsellor" },
  { value: "receptionist", label: "Receptionist" },
  { value: "back office", label: "Back Office" },
  { value: "lecturer", label: "Lecturer" },
  { value: "accounts", label: "Accounts" },
  { value: "accountant", label: "Accountant" },
];

function catalogByPath() {
  const m = new Map();
  for (const row of EMS_ROUTE_CATALOG) {
    m.set(row.path, row);
  }
  return m;
}

const PATH_TO_CATALOG = catalogByPath();

function defaultForm() {
  return {
    name: "",
    key: "",
    icon: "WidgetsOutlined",
    roles: [],
    orderedRouteIds: [],
    pageSearch: "",
  };
}

export default function AppBuilderPage() {
  const [apps, setApps] = useState([]);
  const [defaultApps, setDefaultApps] = useState([]);
  const [defaultRoles, setDefaultRoles] = useState([]);
  const [defaultUsers, setDefaultUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedRoleApp, setSelectedRoleApp] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUserApp, setSelectedUserApp] = useState("");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [wizardStep, setWizardStep] = useState(1);
  const [editApp, setEditApp] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, defaults] = await Promise.all([
        fetchNavApps({ includeMenus: true, refresh: true }),
        fetchAdminDefaultApps(),
      ]);
      setApps(data.apps || []);
      setDefaultApps(defaults.apps || []);
      setDefaultRoles(defaults.role_defaults || []);
      setDefaultUsers(defaults.user_defaults || []);
    } catch (e) {
      toast.error(e.message || "Failed to load apps");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedRole) {
      setSelectedRoleApp("");
      return;
    }
    const row = defaultRoles.find((r) => (r.role_name || "").toLowerCase() === selectedRole.toLowerCase());
    setSelectedRoleApp(row?.app_id || "");
  }, [selectedRole, defaultRoles]);

  useEffect(() => {
    if (!selectedUserId) {
      setSelectedUserApp("");
      return;
    }
    const row = defaultUsers.find((u) => String(u.user_id) === String(selectedUserId));
    setSelectedUserApp(row?.app_id || "");
  }, [selectedUserId, defaultUsers]);

  useEffect(() => {
    load();
  }, [load]);

  const progressPercent = (wizardStep / TOTAL_STEPS) * 100;
  const currentStepMeta = STEP_META[Math.min(Math.max(wizardStep, 1), TOTAL_STEPS) - 1];

  const goToStep = (step) => setWizardStep(step);

  const openWizardCreate = () => {
    setEditApp(null);
    setForm(defaultForm());
    setWizardStep(1);
    setView("wizard");
  };

  const openWizardEdit = (app) => {
    if (app.is_system) {
      toast.info("System apps are not editable here.");
      return;
    }
    const orderedRouteIds = [];
    const items = [...(app.menu_items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    for (const it of items) {
      const cat = PATH_TO_CATALOG.get(it.path);
      if (cat) orderedRouteIds.push(cat.id);
    }
    setEditApp(app);
    setForm({
      name: app.name || "",
      key: app.key || "",
      icon: app.icon || "WidgetsOutlined",
      roles: Array.isArray(app.role_access) ? app.role_access.filter((x) => x && x !== "*") : [],
      orderedRouteIds,
      pageSearch: "",
    });
    setWizardStep(1);
    setView("wizard");
  };

  const closeWizard = () => {
    setView("list");
    setEditApp(null);
    setForm(defaultForm());
    setWizardStep(1);
  };

  const validateStep = (step) => {
    if (step === 1) {
      if (!form.name.trim()) {
        toast.error("Enter an app name.");
        return false;
      }
      if (!/^[a-z0-9][a-z0-9-]{0,62}$/.test((form.key || "").trim())) {
        toast.error("Key must be lowercase letters, numbers, and hyphens (1–63 characters).");
        return false;
      }
    }
    if (step === 3 && form.orderedRouteIds.length === 0) {
      toast.error("Select at least one page for the sidebar.");
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (!validateStep(wizardStep)) return;
    if (wizardStep < TOTAL_STEPS) setWizardStep((s) => s + 1);
  };

  const prevStep = () => {
    if (wizardStep > 1) setWizardStep((s) => s - 1);
    else closeWizard();
  };

  const roleAccessPayload = () => {
    if (!form.roles.length) return ["*"];
    return form.roles;
  };

  const deleteAllMenuItems = async (appId, items) => {
    for (const it of items || []) {
      await deleteMenuItem(it.id);
    }
  };

  const submitWizard = async () => {
    if (!validateStep(3)) {
      setWizardStep(3);
      return;
    }
    if (!validateStep(1)) return;

    setSubmitting(true);
    try {
      const ra = roleAccessPayload();
      let appId = editApp?.id;

      if (!editApp) {
        const created = await createNavApp({
          name: form.name.trim(),
          key: form.key.trim().toLowerCase(),
          icon: form.icon,
          role_access: ra,
        });
        appId = created.app?.id;
        if (!appId) throw new Error("Create app failed");
      } else {
        await updateNavApp(editApp.id, {
          name: form.name.trim(),
          icon: form.icon,
          role_access: ra,
        });
        await deleteAllMenuItems(editApp.id, editApp.menu_items);
      }

      const id = appId;
      for (let i = 0; i < form.orderedRouteIds.length; i += 1) {
        const rid = form.orderedRouteIds[i];
        const row = EMS_ROUTE_CATALOG.find((r) => r.id === rid);
        if (!row) continue;
        await createMenuItem(id, {
          label: row.label,
          path: row.path,
          icon: row.icon,
          order: i,
          role_access: ra,
        });
      }

      toast.success(editApp ? "App updated" : "App created");
      closeWizard();
      await load();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleRoute = (routeId) => {
    setForm((f) => {
      const has = f.orderedRouteIds.includes(routeId);
      if (has) {
        return { ...f, orderedRouteIds: f.orderedRouteIds.filter((x) => x !== routeId) };
      }
      return { ...f, orderedRouteIds: [...f.orderedRouteIds, routeId] };
    });
  };

  const moveRoute = (index, dir) => {
    setForm((f) => {
      const arr = [...f.orderedRouteIds];
      const j = index + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[index], arr[j]] = [arr[j], arr[index]];
      return { ...f, orderedRouteIds: arr };
    });
  };

  const filteredCatalog = useMemo(() => searchRouteCatalog(form.pageSearch), [form.pageSearch]);
  const grouped = useMemo(() => groupCatalogByCategory(filteredCatalog), [filteredCatalog]);

  const selectedRoleOptions = useMemo(
    () => EMS_ROLE_OPTIONS.filter((o) => form.roles.includes(o.value)),
    [form.roles]
  );

  const reviewRows = useMemo(
    () => form.orderedRouteIds.map((id) => EMS_ROUTE_CATALOG.find((r) => r.id === id)).filter(Boolean),
    [form.orderedRouteIds]
  );

  const removeApp = async (appRecord) => {
    if (appRecord.is_system) return;
    if (!window.confirm(`Delete app "${appRecord.name}" and all its menu items?`)) return;
    try {
      await deleteNavApp(appRecord.id);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e.message || "Delete failed");
    }
  };

  if (loading && !apps.length && view === "list") {
    return (
      <Container fluid className={styles.page}>
        <PageLayout title="App Builder" subtitle="Loading…" backTo="/users">
          <div className={styles.wizardWrap}>
            <Card className={`${styles.wizardCard} bg-transparent border-0 shadow-none`}>
              <Card.Body className={`${styles.cardBody} d-flex align-items-center justify-content-center py-5`}>
                <Spinner animation="border" />
              </Card.Body>
            </Card>
          </div>
        </PageLayout>
      </Container>
    );
  }

  if (view === "wizard") {
    return (
      <Container fluid className={styles.page}>
        <PageLayout
          title={editApp ? "Edit custom app" : "Create custom app"}
          subtitle="Step-by-step: name and icon, who can open the app, then which pages appear in the sidebar."
          onBack={prevStep}
          backLabel={wizardStep === 1 ? "← Back to list" : "← Back"}
        >
          <div className={`${styles.wizardWrap} ${styles.wizardWrapFullWidth}`}>
            <Card className={`${styles.wizardCard} bg-transparent border-0 shadow-none`}>
              <Card.Body className={styles.cardBody}>
                <header className={styles.gwHeader}>
                  <div className={styles.gwProgressIndicator}>
                    <div className={styles.gwProgressText} id="app-builder-step-heading">
                      Step {wizardStep} of {TOTAL_STEPS}
                      {currentStepMeta?.title ? (
                        <>
                          <span className={styles.gwProgressSep} aria-hidden="true">
                            {" "}
                            ·{" "}
                          </span>
                          <span className={styles.gwProgressStepName}>{currentStepMeta.title}</span>
                        </>
                      ) : null}
                    </div>
                    <ProgressBar now={progressPercent} className={styles.gwLinearProgress} aria-labelledby="app-builder-step-heading" />
                  </div>
                </header>

                <nav className={styles.gwNav} aria-label="Wizard steps">
                  <div className={styles.gwProgress}>
                    {STEP_META.map((step, idx) => {
                      const stepNumber = idx + 1;
                      const status = stepNumber < wizardStep ? "done" : stepNumber === wizardStep ? "current" : "locked";
                      const stepClass =
                        status === "done" ? styles.gwStepDone : status === "current" ? styles.gwStepCurrent : styles.gwStepLocked;
                      const badgeClass =
                        status === "done"
                          ? styles.gwStepBadgeDone
                          : status === "current"
                            ? styles.gwStepBadgeCurrent
                            : styles.gwStepBadgeLocked;
                      const shortClass =
                        status === "done"
                          ? styles.gwStepShortDone
                          : status === "current"
                            ? styles.gwStepShortCurrent
                            : styles.gwStepShortLocked;
                      return (
                        <button
                          key={step.id}
                          type="button"
                          className={`${styles.gwStep} ${stepClass}`}
                          onClick={() => goToStep(stepNumber)}
                          disabled={stepNumber > wizardStep}
                          aria-label={`Go to step ${stepNumber}: ${step.title}`}
                        >
                          <div className={`${styles.gwStepBadge} ${badgeClass}`}>
                            {status === "done" ? <CheckCircleRoundedIcon fontSize="small" /> : step.icon}
                          </div>
                          <div className={styles.gwStepLabel}>
                            <div className={`${styles.gwStepShort} ${shortClass}`}>{step.short}</div>
                            <div className={styles.gwStepTitle}>{step.title}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </nav>

                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (wizardStep === TOTAL_STEPS) submitWizard();
                    else nextStep();
                  }}
                  noValidate
                >
                  <section className={styles.stepBody} aria-live="polite">
                    {wizardStep === 1 && (
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Group>
                            <Form.Label>App name *</Form.Label>
                            <Form.Control
                              type="text"
                              value={form.name}
                              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                              placeholder="e.g. Recruitment hub"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group>
                            <Form.Label>URL key (slug) *</Form.Label>
                            <Form.Control
                              type="text"
                              value={form.key}
                              onChange={(e) => setForm((f) => ({ ...f, key: e.target.value.toLowerCase() }))}
                              placeholder="e.g. recruitment-hub"
                              disabled={Boolean(editApp)}
                            />
                            <Form.Text className="text-muted">Used internally; cannot be changed after creation.</Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Label className="d-block mb-2">Launcher icon *</Form.Label>
                          <div
                            className="d-flex flex-wrap gap-2 p-2 rounded border"
                            style={{ maxHeight: 280, overflowY: "auto", borderColor: "var(--bs-border-color, #dee2e6)" }}
                          >
                            {NAV_ICON_OPTIONS.map((name) => {
                              const Icon = getNavIconComponent(name);
                              const sel = form.icon === name;
                              return (
                                <button
                                  key={name}
                                  type="button"
                                  className={`btn btn-sm d-flex flex-column align-items-center justify-content-center p-2 ${sel ? "border-primary border-2" : "border"}`}
                                  style={{ width: 72, minHeight: 72 }}
                                  onClick={() => setForm((f) => ({ ...f, icon: name }))}
                                  title={name}
                                >
                                  <Icon fontSize="medium" />
                                  <span className="text-muted" style={{ fontSize: 9, marginTop: 4, lineHeight: 1.1, wordBreak: "break-all" }}>
                                    {name.replace(/Outlined$/, "")}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </Col>
                      </Row>
                    )}

                    {wizardStep === 2 && (
                      <Row className="g-3">
                        <Col md={12}>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            Choose which roles can see this app in the launcher. Leave empty to allow <strong>all</strong> roles.
                          </Typography>
                          <Autocomplete
                            multiple
                            options={EMS_ROLE_OPTIONS}
                            getOptionLabel={(o) => o.label}
                            value={selectedRoleOptions}
                            onChange={(_, v) =>
                              setForm((f) => ({ ...f, roles: (v || []).map((x) => x.value) }))
                            }
                            renderInput={(params) => (
                              <TextField {...params} label="Roles" placeholder="Search or pick roles" />
                            )}
                            disableCloseOnSelect
                          />
                        </Col>
                      </Row>
                    )}

                    {wizardStep === 3 && (
                      <Row className="g-3">
                        <Col md={12}>
                          <TextField
                            fullWidth
                            label="Search pages"
                            placeholder="Type to filter by name, path, or category…"
                            value={form.pageSearch}
                            onChange={(e) => setForm((f) => ({ ...f, pageSearch: e.target.value }))}
                            margin="normal"
                            size="small"
                          />
                        </Col>
                        <Col md={12}>
                          <Typography variant="subtitle2" gutterBottom>
                            Selected order ({form.orderedRouteIds.length})
                          </Typography>
                          {form.orderedRouteIds.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No pages selected yet. Check entries below.
                            </Typography>
                          ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
                              {form.orderedRouteIds.map((rid, idx) => {
                                const row = EMS_ROUTE_CATALOG.find((r) => r.id === rid);
                                if (!row) return null;
                                const Icon = getNavIconComponent(row.icon);
                                return (
                                  <Box
                                    key={rid}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      py: 0.5,
                                      px: 1,
                                      borderRadius: 1,
                                      bgcolor: "action.hover",
                                    }}
                                  >
                                    <Icon fontSize="small" />
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                      {row.label}{" "}
                                      <Typography component="span" variant="caption" color="text.secondary">
                                        {row.path}
                                      </Typography>
                                    </Typography>
                                    <Button size="sm" variant="outline-secondary" type="button" disabled={idx === 0} onClick={() => moveRoute(idx, -1)}>
                                      Up
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      type="button"
                                      disabled={idx === form.orderedRouteIds.length - 1}
                                      onClick={() => moveRoute(idx, 1)}
                                    >
                                      Down
                                    </Button>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </Col>
                        <Col md={12}>
                          <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid var(--bs-border-color, #dee2e6)", borderRadius: 8, padding: 12 }}>
                            {grouped.map(([category, rows]) => (
                              <div key={category} className="mb-3">
                                <div className="fw-semibold text-muted small mb-2">{category}</div>
                                {rows.map((row) => {
                                  const checked = form.orderedRouteIds.includes(row.id);
                                  const Icon = getNavIconComponent(row.icon);
                                  return (
                                    <FormControlLabel
                                      key={row.id}
                                      control={
                                        <Checkbox
                                          checked={checked}
                                          onChange={() => toggleRoute(row.id)}
                                          size="small"
                                        />
                                      }
                                      label={
                                        <span className="d-inline-flex align-items-center gap-2">
                                          <Icon fontSize="small" />
                                          <span>
                                            {row.label}
                                            <span className="text-muted small ms-1">{row.path}</span>
                                          </span>
                                        </span>
                                      }
                                      sx={{ display: "flex", alignItems: "flex-start", ml: 0, mb: 0.5 }}
                                    />
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        </Col>
                      </Row>
                    )}

                    {wizardStep === 4 && (
                      <Row className="g-3">
                        <Col md={12}>
                          <Typography variant="subtitle1" gutterBottom>
                            {form.name || "Untitled app"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            Key: <code>{form.key || "—"}</code> · Icon: {form.icon}
                          </Typography>
                          <Typography variant="body2" paragraph>
                            <strong>Roles:</strong>{" "}
                            {form.roles.length ? form.roles.join(", ") : "Everyone (*)"}
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>
                            Sidebar pages ({reviewRows.length})
                          </Typography>
                          <ul className="mb-0">
                            {reviewRows.map((r) => (
                              <li key={r.id}>
                                {r.label} — <code>{r.path}</code>
                              </li>
                            ))}
                          </ul>
                        </Col>
                      </Row>
                    )}
                  </section>

                  <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                    <Button type="button" variant="outline-secondary" onClick={prevStep}>
                      {wizardStep === 1 ? "Cancel" : "Back"}
                    </Button>
                    <div className="d-flex gap-2">
                      {wizardStep < TOTAL_STEPS ? (
                        <Button type="submit" variant="primary">
                          Next
                        </Button>
                      ) : (
                        <Button type="submit" variant="primary" disabled={submitting}>
                          {submitting ? "Saving…" : editApp ? "Save changes" : "Create app"}
                        </Button>
                      )}
                    </div>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </div>
        </PageLayout>
      </Container>
    );
  }

  return (
    <Container fluid className={styles.page}>
      <PageLayout
        title="App Builder"
        subtitle="Create custom apps with a guided flow. Links point to existing EMS pages."
        backTo="/users"
      >
        <div className={styles.wizardWrap}>
          <Card className={`${styles.wizardCard} bg-transparent border-0 shadow-none`}>
            <Card.Body className={styles.cardBody}>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <Typography variant="body2" color="text.secondary" className="mb-0">
                  System apps are listed for reference; only custom apps can be edited or deleted.
                </Typography>
                <Button variant="primary" onClick={openWizardCreate}>
                  New custom app (wizard)
                </Button>
              </div>

              <Card className="mb-3">
                <Card.Body>
                  <Typography variant="subtitle1" className="mb-3">
                    Default app assignments
                  </Typography>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label>Default app by role</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
                          <option value="">Select role</option>
                          {(new Set((defaultUsers || []).flatMap((u) => u.roles || []))).size
                            ? Array.from(new Set((defaultUsers || []).flatMap((u) => u.roles || []))).sort().map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))
                            : null}
                        </Form.Select>
                        <Form.Select
                          value={selectedRoleApp}
                          onChange={(e) => setSelectedRoleApp(e.target.value)}
                          disabled={!selectedRole}
                        >
                          <option value="">No role default</option>
                          {(defaultApps || []).map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                            </option>
                          ))}
                        </Form.Select>
                        <Button
                          type="button"
                          variant="outline-primary"
                          disabled={!selectedRole}
                          onClick={async () => {
                            try {
                              await setRoleDefaultApp(selectedRole, selectedRoleApp || null);
                              toast.success("Role default saved");
                              await load();
                            } catch (e) {
                              toast.error(e.message || "Failed to save role default");
                            }
                          }}
                        >
                          Save
                        </Button>
                      </div>
                    </Col>
                    <Col md={6}>
                      <Form.Label>Default app by user</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}>
                          <option value="">Select user</option>
                          {(defaultUsers || []).map((u) => (
                            <option key={u.user_id} value={u.user_id}>
                              {u.username}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Select
                          value={selectedUserApp}
                          onChange={(e) => setSelectedUserApp(e.target.value)}
                          disabled={!selectedUserId}
                        >
                          <option value="">No user override</option>
                          {(defaultApps || []).map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                            </option>
                          ))}
                        </Form.Select>
                        <Button
                          type="button"
                          variant="outline-primary"
                          disabled={!selectedUserId}
                          onClick={async () => {
                            try {
                              await setUserDefaultApp(Number(selectedUserId), selectedUserApp || null);
                              toast.success("User default saved");
                              await load();
                            } catch (e) {
                              toast.error(e.message || "Failed to save user default");
                            }
                          }}
                        >
                          Save
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Table responsive hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Key</th>
                    <th>System</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(apps || []).map((a) => (
                    <tr key={a.id}>
                      <td>{a.name}</td>
                      <td>
                        <code>{a.key}</code>
                      </td>
                      <td>{a.is_system ? "Yes" : "No"}</td>
                      <td className="text-end text-nowrap">
                        {!a.is_system && (
                          <>
                            <Button size="sm" variant="outline-primary" className="me-2" onClick={() => openWizardEdit(a)}>
                              Edit
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => removeApp(a)}>
                              Delete
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </div>
      </PageLayout>
    </Container>
  );
}
